import { Button, Card, Spinner, Stack } from "react-bootstrap";
import {
  AdvancedMarker,
  APIProvider,
  Map,
  useMap,
  Pin,
  InfoWindow,
  useAdvancedMarkerRef,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import React, { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "../utilities/toast";

const formatLocationLabel = (location) => {
  const latitude = location?.lat ?? location?.latitude;
  const longitude = location?.lng ?? location?.longitude;

  if (typeof latitude !== "number" || typeof longitude !== "number") {
    return "Waiting for location...";
  }

  return `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`;
};

const normalizeLocation = (location) => {
  if (!location) return null;

  if (typeof location.lat === "function" && typeof location.lng === "function") {
    return {
      lat: location.lat(),
      lng: location.lng(),
    };
  }

  const latitude = location?.lat ?? location?.latitude;
  const longitude = location?.lng ?? location?.longitude;

  if (typeof latitude !== "number" || typeof longitude !== "number") {
    return null;
  }

  return { lat: latitude, lng: longitude };
};

const isZeroResultsError = (error) => {
  const statusCode = error?.status || error?.code;
  const message = typeof error?.message === "string" ? error.message : "";

  return statusCode === "ZERO_RESULTS" || message.includes("ZERO_RESULTS");
};

const createEmptyRouteDetails = () => ({
  start: "",
  end: "",
  duration: { text: "", value: 0 },
  distance: { text: "", value: 0 },
});

const ROUTE_REQUEST_TIMEOUT_MS = 10000;

// Directions calls are billed per request, and the pin-drag preview can fire on every
// "center_changed" tick. These throttle the request rate the way Uber/Glovo-style pickup
// pin flows do: wait for the drag to pause, skip re-routing for jitter-sized moves, and
// reuse a recent response instead of re-querying the same origin/destination pair.
const DIRECTIONS_DEBOUNCE_MS = 450;
const MIN_REROUTE_DISTANCE_METERS = 25;
const ROUTE_CACHE_TTL_MS = 3 * 60 * 1000;
const ROUTE_CACHE_MAX_ENTRIES = 30;
const EARTH_RADIUS_METERS = 6371000;

const toRadians = (degrees) => (degrees * Math.PI) / 180;

const haversineDistanceMeters = (a, b) => {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const lat1 = toRadians(a.lat);
  const lat2 = toRadians(b.lat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h = sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h));
};

// Coordinates are rounded to ~11m so near-identical drag stops share a cache entry.
const buildRouteCacheKey = (origin, destination) =>
  `${origin.lat.toFixed(4)},${origin.lng.toFixed(4)}|${destination.lat.toFixed(4)},${destination.lng.toFixed(4)}`;

// Module-level so the cache survives remounts (e.g. checkout re-render) within the tab session.
// `Map` the identifier is shadowed by the `Map` component imported above, so the
// global constructor must be referenced explicitly here.
const routeResponseCache = new globalThis.Map();

const getCachedRoute = (key) => {
  const cached = routeResponseCache.get(key);
  if (!cached) return undefined;
  if (Date.now() - cached.timestamp > ROUTE_CACHE_TTL_MS) {
    routeResponseCache.delete(key);
    return undefined;
  }
  routeResponseCache.delete(key);
  routeResponseCache.set(key, cached);
  return cached;
};

const setCachedRoute = (key, leg) => {
  if (routeResponseCache.size >= ROUTE_CACHE_MAX_ENTRIES) {
    const oldestKey = routeResponseCache.keys().next().value;
    routeResponseCache.delete(oldestKey);
  }
  routeResponseCache.set(key, { leg, timestamp: Date.now() });
};

// Test-only: the cache is module-level (by design, so it survives remounts), so tests
// need a way to clear it between cases instead of relying on incidental TTL expiry.
export const __clearDirectionsRouteCacheForTests = () => {
  routeResponseCache.clear();
};

const createRoutePayload = ({ leg, userLocation, storeLocation }) => ({
  leg,
  userLocation,
  storeLocation,
  details: leg
    ? {
        start: leg.start_address || "",
        end: leg.end_address || "",
        duration: {
          text: leg.duration?.text || "",
          value: typeof leg.duration?.value === "number" ? leg.duration.value : 0,
        },
        distance: {
          text: leg.distance?.text || "",
          value: typeof leg.distance?.value === "number" ? leg.distance.value : 0,
        },
      }
    : createEmptyRouteDetails(),
});

function MapControl({ isRefreshing, isUpdatingLocation, location, onRefresh }) {
  const isBusy = isRefreshing || isUpdatingLocation;
  const locationText = isUpdatingLocation ? "Updating location..." : formatLocationLabel(location);

  return (
    <div className="map-overlay-controls">
      <div className="map-control-stack">
        <div className="map-control-pill map-control-pill--action" aria-live="polite">
          <Button
            variant="light"
            size="sm"
            onClick={onRefresh}
            disabled={isBusy}
            className="refresh-btn shadow-sm"
            style={{
              backgroundColor: "white",
              border: "1px solid #dee2e6",
              borderRadius: "10px",
              minWidth: "36px",
              minHeight: "36px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.35rem",
              color: "#111827",
            }}
            title={isUpdatingLocation ? "Updating pinned location" : "Refresh location"}
            data-testid="map-control-refresh"
          >
            {isBusy ? (
              <Spinner animation="border" size="sm" style={{ width: 14, height: 14 }} />
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" viewBox="0 0 16 16">
                <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
              </svg>
            )}
          </Button>
        </div>

        {/* <div className="map-control-location" aria-live="polite">
          <div className="map-control-location__content">
            <span className="map-control-location__label">Pinned location</span>
            <span className="map-control-location__value" data-testid="map-control-location">
              {locationText}
            </span>
          </div>
        </div> */}
      </div>
    </div>
  );
}

const MapInterface = ({
  userLocation,
  setUserLocation,
  storeLocation,
  setRouteLeg,
  onRouteChange,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isUpdatingLocation, setIsUpdatingLocation] = useState(false);
  const [isRouteLoading, setIsRouteLoading] = useState(false);
  const [currentLeg, setCurrentLeg] = useState(null);
  const [currentLegLocation, setCurrentLegLocation] = useState(null);
  const [routeErrorCode, setRouteErrorCode] = useState(null);
  const [error, setError] = useState(null);
  const [liveLocation, setLiveLocation] = useState(userLocation);
  const [confirmedLocation, setConfirmedLocation] = useState(userLocation);

  const getLocationKey = useCallback((location) => {
    const latitude = location?.lat ?? location?.latitude;
    const longitude = location?.lng ?? location?.longitude;

    if (typeof latitude !== "number" || typeof longitude !== "number") {
      return null;
    }

    return `${latitude.toFixed(5)}:${longitude.toFixed(5)}`;
  }, []);

  const handleLegChange = useCallback((nextLeg, resolvedLocation) => {
    setCurrentLeg(nextLeg);
    setCurrentLegLocation(resolvedLocation);
  }, []);

  // console.log("Current leg:", currentLeg);

  const legMatchesLiveLocation =
    Boolean(currentLeg) &&
    Boolean(currentLegLocation) &&
    getLocationKey(currentLegLocation) === getLocationKey(liveLocation);

  const routeDurationText = isRouteLoading
    ? "Calculating route..."
    : routeErrorCode === "ZERO_RESULTS"
      ? "No route found"
      : legMatchesLiveLocation
        ? currentLeg?.duration?.text
        : "Route...";
  const routeAddressText = legMatchesLiveLocation
    ? currentLeg?.end_address || formatLocationLabel(liveLocation)
    : formatLocationLabel(liveLocation);

  const handleLocationConfirmed = useCallback(
    (location) => {
      setConfirmedLocation(location);
      setLiveLocation(location);
      setCurrentLeg(null);
      setCurrentLegLocation(null);
      setRouteErrorCode(null);
      if (setUserLocation) {
        setUserLocation(location);
      }
    },
    [setUserLocation]
  );

  const mapDivStyles = {
    height: "360px",
    backgroundColor: "#e5e7eb",
    transition: "height 0.4s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.3s ease",
    position: "relative",
    borderRadius: "12px",
    boxShadow: "0 2px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)",
    overflow: "hidden",
  };

  useEffect(() => {
    // Simulate loading time for map
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [userLocation, storeLocation]);

  useEffect(() => {
    setLiveLocation(userLocation);
    setConfirmedLocation(userLocation);
  }, [userLocation]);

  const handleRefresh = async () => {
    try {
      setIsRefreshing(true);
      
      // Get fresh location data
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            setUserLocation({ lat: latitude, lng: longitude });
            // toast.success("Location refreshed successfully");
          },
          (error) => {
            console.error("Error getting location:", error);
            toast.error("Failed to refresh location");
            setError("Failed to get your location. Please try again.");
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0
          }
        );
      } else {
        toast.error("Geolocation is not supported by your browser");
        setError("Geolocation is not supported");
      }
    } catch (error) {
      console.error("Error refreshing map:", error);
      toast.error("Failed to refresh map");
      setError("Failed to refresh map. Please try again.");
    } finally {
      // Keep the map mounted while the control reflects refresh progress.
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  };


  const renderMapSkeleton = () => (
    <div
      className="d-block position-relative"
      style={{
        height: "360px",
        borderRadius: "12px",
        backgroundColor: "#e5e7eb",
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.04)",
      }}
    >
      <div className="skeleton-map">map-control-pill
        <div className="skeleton-marker" style={{ top: '38%', left: '42%' }} />
        <div className="skeleton-marker" style={{ top: '55%', left: '58%' }} />
      </div>
    </div>
  );

  if (error) {
    return (
      <Stack>
        <div>
          <h6 className="text-dark">Mark your location</h6>
          <p className="text-muted">Help the courier reach you faster.</p>
        </div>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-4">
            <div className="mb-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="48"
                height="48"
                fill="currentColor"
                className="bi bi-exclamation-circle text-danger"
                viewBox="0 0 16 16"
              >
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
              </svg>
            </div>
            <h6 className="text-danger mb-2">Map Loading Error</h6>
            <p className="text-muted mb-3">{error}</p>
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => {
                setError(null);
                setIsLoading(true);
                // Retry loading the map
                setTimeout(() => {
                  setIsLoading(false);
                }, 1000);
              }}
            >
              Try Again
            </Button>
          </Card.Body>
        </Card>
      </Stack>
    );
  }

  return (
    <Stack gap={3}>
      <div>
        <h6 className="text-dark mb-1" style={{ fontWeight: 600, letterSpacing: "-0.01em" }}>
          Mark your location
        </h6>
        <p className="text-muted mb-0" style={{ fontSize: "0.875rem" }}>
          Help the courier reach you faster.
        </p>
      </div>
      {isLoading ? (
        renderMapSkeleton()
      ) : (
        <div style={mapDivStyles}>
          <APIProvider apiKey={import.meta.env.VITE_MAP_API_KEY}>
            <Map
              defaultZoom={13}
              defaultCenter={{
                lat: userLocation.lat,
                lng: userLocation.lng,
              }}
              mapId={import.meta.env.VITE_MAP_ID}
              gestureHandling="greedy"
              fullscreenControl={true}
              onError={(error) => {
                console.error("Map error:", error);
                setError("Failed to load map. Please try again.");
                toast.error("Failed to load map");
              }}
            >
              <PoiMarker
                pois={userLocation}
                onLocationChanged={handleLocationConfirmed}
                onDraggingChange={setIsUpdatingLocation}
                onLocationPreviewChange={setLiveLocation}
              />
              <Directions
                storeLocation={storeLocation}
                userLocation={liveLocation}
                setRouteLeg={setRouteLeg}
                onRouteChange={onRouteChange}
                onLoadingChange={setIsRouteLoading}
                onLegChange={handleLegChange}
                onRouteErrorChange={setRouteErrorCode}
              />
            </Map>
          </APIProvider>

          <MapControl
            isRefreshing={isRefreshing}
            isUpdatingLocation={isUpdatingLocation}
            location={liveLocation ?? userLocation}
            onRefresh={handleRefresh}
          />

          <div className="route-info-control" aria-live="polite">
            {currentLeg && !isRouteLoading && legMatchesLiveLocation ? (
              <div className="route-info-pill">
                <div className="route-info-pill__icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#6b7280" viewBox="0 0 16 16">
                    <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
                    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
                  </svg>
                </div>
                <div className="route-info-pill__content">
                  <span className="route-info-pill__duration">{routeDurationText}</span>
                  <span className="route-info-pill__divider">·</span>
                  <span className="route-info-pill__address">{routeAddressText}</span>
                </div>
              </div>
            ) : (
              <div className="route-info-pill">
                <div className="route-info-pill__icon" aria-hidden="true">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="#6b7280" viewBox="0 0 16 16">
                    <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71V3.5z"/>
                    <path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0z"/>
                  </svg>
                </div>
                <div className="route-info-pill__content">
                  <span className="route-info-pill__duration">{routeDurationText}</span>
                  {routeAddressText && routeAddressText !== routeDurationText ? (
                    <>
                      <span className="route-info-pill__divider">·</span>
                      <span className="route-info-pill__address">{routeAddressText}</span>
                    </>
                  ) : null}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </Stack>
  );
};

function Directions({ storeLocation, userLocation, setRouteLeg, onRouteChange, onLegChange, onLoadingChange, onRouteErrorChange }) {
  const routesLibrary = useMapsLibrary("routes");
  const [directionService, setDirectionService] = useState();
  const latestRequestRef = useRef(0);
  const lastResolvedRef = useRef(null);

  useEffect(() => {
    if (!routesLibrary) {
      console.log("routesLibrary not loaded");
      return;
    }
    if (!directionService) {
      setDirectionService(new routesLibrary.DirectionsService());
      console.log("DirectionsService initialized");
      return;
    }
  }, [routesLibrary, directionService]);

  useEffect(() => {
    const bailOut = (reason, details, { transient = false } = {}) => {
      const log = transient ? console.log : console.error;
      log(`[Directions] route not requested — ${reason}`, details ?? { userLocation, storeLocation });
      onRouteChange?.(createRoutePayload({ leg: null, userLocation, storeLocation }));
      onLoadingChange?.(false);
    };

    if (!directionService) {
      // Expected on the first render or two while the "routes" library script loads —
      // the effect re-runs and proceeds once DirectionsService finishes initializing.
      bailOut("DirectionsService not ready yet", null, { transient: true });
      return;
    }
    if (!userLocation) {
      bailOut("userLocation is missing");
      return;
    }
    if (!storeLocation) {
      bailOut("storeLocation is missing");
      return;
    }
    const storeLat = storeLocation.lat ?? storeLocation.latitude;
    const storeLng = storeLocation.lng ?? storeLocation.longitude;
    const userLat = userLocation.lat ?? userLocation.latitude;
    const userLng = userLocation.lng ?? userLocation.longitude;

    if (typeof storeLat !== "number" || typeof storeLng !== "number") {
      bailOut("storeLocation lat/lng are not numbers", storeLocation);
      return;
    }
    if (typeof userLat !== "number" || typeof userLng !== "number") {
      bailOut("userLocation lat/lng are not numbers", userLocation);
      return;
    }
    if (userLat === 0 || userLng === 0) {
      bailOut("userLocation lat/lng is zero", userLocation);
      return;
    }
    if (storeLat === 0 || storeLng === 0) {
      bailOut("storeLocation lat/lng is zero", storeLocation);
      return;
    }

    const origin = { lat: storeLat, lng: storeLng };
    const destination = { lat: userLat, lng: userLng };

    const applyResolvedLeg = (leg) => {
      const payload = createRoutePayload({ leg, userLocation: destination, storeLocation: origin });
      setRouteLeg(leg);
      onRouteChange?.(payload);
      onLoadingChange?.(false);
      onRouteErrorChange?.(leg ? null : "ZERO_RESULTS");
      onLegChange?.(leg, destination);
    };

    const lastResolved = lastResolvedRef.current;
    const hasMovedEnough =
      !lastResolved ||
      haversineDistanceMeters(origin, lastResolved.origin) >= MIN_REROUTE_DISTANCE_METERS ||
      haversineDistanceMeters(destination, lastResolved.destination) >= MIN_REROUTE_DISTANCE_METERS;

    if (!hasMovedEnough) {
      // Drag jitter within ~25m — reuse the last route instead of re-billing the API.
      applyResolvedLeg(lastResolved.leg);
      return;
    }

    const cacheKey = buildRouteCacheKey(origin, destination);
    const cached = getCachedRoute(cacheKey);
    if (cached) {
      lastResolvedRef.current = { origin, destination, leg: cached.leg };
      applyResolvedLeg(cached.leg);
      return;
    }

    onLoadingChange?.(true);
    onRouteErrorChange?.(null);

    const debounceTimer = setTimeout(() => {
      const requestId = latestRequestRef.current + 1;
      latestRequestRef.current = requestId;
      const start = new google.maps.LatLng(storeLat, storeLng);
      const end = new google.maps.LatLng(userLat, userLng);

      let timeoutId;
      const timeout = new Promise((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error("Directions request timed out")),
          ROUTE_REQUEST_TIMEOUT_MS
        );
      });

      Promise.race([
        Promise.resolve().then(() =>
          directionService.route({
            origin: start,
            destination: end,
            travelMode: google.maps.TravelMode.DRIVING,
          })
        ),
        timeout,
      ])
        .then((response) => {
          if (requestId !== latestRequestRef.current) {
            console.error(
              `[Directions] discarding stale response for request #${requestId} (latest is #${latestRequestRef.current})`,
              { origin: { lat: storeLat, lng: storeLng }, destination: { lat: userLat, lng: userLng } }
            );
            return;
          }

          const nextLeg = response?.routes[0]?.legs[0] || null;
          const nextRoutePayload = createRoutePayload({
            leg: nextLeg,
            userLocation: { lat: userLat, lng: userLng },
            storeLocation: { lat: storeLat, lng: storeLng },
          });

          lastResolvedRef.current = { origin, destination, leg: nextLeg };
          setCachedRoute(cacheKey, nextLeg);

          setRouteLeg(nextLeg);
          onRouteChange?.(nextRoutePayload);
          onLoadingChange?.(false);
          onRouteErrorChange?.(null);
          if (onLegChange) onLegChange(nextLeg, { lat: userLat, lng: userLng });
        })
        .catch((error) => {
          if (requestId !== latestRequestRef.current) {
            console.error(
              `[Directions] discarding stale rejection for request #${requestId} (latest is #${latestRequestRef.current})`,
              { origin: { lat: storeLat, lng: storeLng }, destination: { lat: userLat, lng: userLng } }
            );
            return;
          }

          onLoadingChange?.(false);

          if (isZeroResultsError(error)) {
            const emptyRoutePayload = createRoutePayload({
              leg: null,
              userLocation: { lat: userLat, lng: userLng },
              storeLocation: { lat: storeLat, lng: storeLng },
            });

            lastResolvedRef.current = { origin, destination, leg: null };
            setCachedRoute(cacheKey, null);

            setRouteLeg(null);
            onRouteChange?.(emptyRoutePayload);
            onRouteErrorChange?.("ZERO_RESULTS");
            if (onLegChange) onLegChange(null, { lat: userLat, lng: userLng });
            console.error(
              "[Directions] ZERO_RESULTS — Google found no driving route between these points:",
              { origin: { lat: storeLat, lng: storeLng }, destination: { lat: userLat, lng: userLng } }
            );
            return;
          }

          onRouteErrorChange?.("REQUEST_FAILED");
          console.error(
            "[Directions] request failed:",
            error,
            { origin: { lat: storeLat, lng: storeLng }, destination: { lat: userLat, lng: userLng } }
          );
        })
        .finally(() => clearTimeout(timeoutId));
    }, DIRECTIONS_DEBOUNCE_MS);

    return () => clearTimeout(debounceTimer);
  }, [directionService, userLocation, storeLocation, setRouteLeg, onRouteChange, onLegChange, onLoadingChange, onRouteErrorChange]);

  return null;
}

const PoiMarker = ({ pois, onLocationChanged, onDraggingChange, onLocationPreviewChange }) => {
  const map = useMap();
  const [markerRef, marker] = useAdvancedMarkerRef();
  const [infoWindowShown, setInfoWindowShown] = useState(true);
  const [draftLocation, setDraftLocation] = useState(pois);
  const isMapDraggingRef = useRef(false);
  const draftLocationRef = useRef(draftLocation);

  useEffect(() => {
    draftLocationRef.current = draftLocation;
  }, [draftLocation]);

  useEffect(() => {
    if (!pois) return;

    const incoming = normalizeLocation(pois);
    const current = normalizeLocation(draftLocationRef.current);
    const isEchoOfCurrentPosition =
      incoming && current && incoming.lat === current.lat && incoming.lng === current.lng;

    if (isEchoOfCurrentPosition) return;

    setDraftLocation(pois);
    if (map) {
      map.panTo(pois);
    }
  }, [map, pois]);

  const handleSetLocation = useCallback(
    (location = draftLocationRef.current) => {
      const normalizedLocation = normalizeLocation(location) ?? draftLocationRef.current;
      onLocationPreviewChange?.(normalizedLocation);
      onLocationChanged?.(normalizedLocation);
      onDraggingChange?.(false);
    },
    [onDraggingChange, onLocationChanged, onLocationPreviewChange]
  );

  useEffect(() => {
    if (!map) return;

    const syncCenterWithMarker = () => {
      const center = map.getCenter();
      if (!center) return null;
      const nextLocation = center.toJSON();
      setDraftLocation(nextLocation);
      onLocationPreviewChange?.(nextLocation);
      return nextLocation;
    };

    syncCenterWithMarker();

    const centerChangedListener = map.addListener("center_changed", syncCenterWithMarker);
    const dragStartListener = map.addListener("dragstart", () => {
      isMapDraggingRef.current = true;
      setInfoWindowShown(false);
      onDraggingChange?.(true);
    });
    const idleListener = map.addListener("idle", () => {
      const settledLocation = syncCenterWithMarker();
      setInfoWindowShown(false);

      if (isMapDraggingRef.current && settledLocation) {
        isMapDraggingRef.current = false;
        handleSetLocation(settledLocation);
      }
    });

    return () => {
      centerChangedListener.remove();
      dragStartListener.remove();
      idleListener.remove();
    };
  }, [map, onDraggingChange, onLocationPreviewChange, handleSetLocation]);

  const handleClick = useCallback(() => {
    setInfoWindowShown(false);
  }, []);

  return (
    <>
      <AdvancedMarker
        key="meruKenya"
        position={draftLocation}
        ref={markerRef}
        clickable={true}
        gmpDraggable={false}
        onClick={handleClick}
      >
        <Pin background={"#dc3545"} glyphColor={"#fff"} borderColor={"#000"} />
      </AdvancedMarker>
      {infoWindowShown && (
        <InfoWindow
          anchor={marker}
          headerDisabled
          disableAutoPan
          onCloseClick={() => setInfoWindowShown(false)}
        >
        <div style={{ width: "200px", padding: "4px 2px" }}>
          <p style={{ fontSize: "0.78rem", color: "#6b7280", marginBottom: "10px", lineHeight: 1.4 }}>
            Drag the map so the pin lands on your exact spot.
          </p>
          <Button
            onClick={() => handleSetLocation()}
            variant="primary"
            size="sm"
            className="w-100"
            style={{ borderRadius: "8px", fontWeight: 500, fontSize: "0.82rem" }}
          >
            Confirm Location
          </Button>
        </div>
        </InfoWindow>
      )}
    </>
  );
};

// Update the styles
const styles = `
.skeleton-map {
  position: relative;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, #e5e7eb 25%, #d1d5db 50%, #e5e7eb 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.skeleton-marker {
  position: absolute;
  width: 24px;
  height: 24px;
  background: #9ca3af;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.skeleton-marker::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 12px;
  height: 12px;
  background: #6b7280;
  border-radius: 50%;
  transform: translate(-50%, -50%);
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.refresh-btn {
  transition: all 0.2s ease-in-out;
}

.refresh-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.refresh-btn:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.map-overlay-controls {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  pointer-events: none;
  z-index: 2147483000;
}

.map-control-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
}

.map-control-pill,
.map-control-location {
  display: flex;
  align-items: center;
  gap: 10px;
  max-width: min(88vw, 260px);
  padding: 8px 10px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid rgba(229, 231, 235, 0.9);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  pointer-events: auto;
}

.map-control-pill--action {
  align-self: flex-end;
}

.map-overlay-controls .refresh-btn {
  pointer-events: auto;
  flex-shrink: 0;
}

.map-control-location__content {
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.map-control-location__label {
  font-size: 0.68rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #6b7280;
}

.map-control-location__value {
  font-size: 0.8rem;
  font-weight: 600;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

@media (max-width: 768px) {
  .map-control-pill,
  .map-control-location {
    gap: 8px;
    max-width: min(90vw, 240px);
    padding: 7px 8px;
  }

  .refresh-btn {
    justify-content: center;
    padding: 0.375rem;
    border-radius: 50%;
  }

  .refresh-btn svg {
    margin: 0;
    padding: 0;
  }

  .map-control-location__value {
    font-size: 0.76rem;
  }
}

.route-info-control {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  pointer-events: none;
  padding: 0 12px 14px;
  z-index: 2147482990;
}

.route-info-pill {
  display: flex;
  align-items: center;
  gap: 8px;
  width: min(100%, 320px);
  max-width: 320px;
  padding: 9px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.75);
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.15);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  color: #374151;
  pointer-events: auto;
}

.route-info-pill__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(107, 114, 128, 0.12);
}

.route-info-pill__content {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
}

.route-info-pill__duration {
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
}

.route-info-pill__divider {
  color: #9ca3af;
  flex-shrink: 0;
}

.route-info-pill__address {
  font-size: 0.74rem;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}

@media (max-width: 480px) {
  .route-info-control {
    padding: 0 10px 12px;
  }

  .route-info-pill {
    width: min(100%, 292px);
    gap: 7px;
    padding: 8px 10px;
  }

  .route-info-pill__duration {
    font-size: 0.76rem;
  }

  .route-info-pill__address {
    font-size: 0.7rem;
  }
}
`;

// Add the styles to the document
const mapInterfaceStyleId = "teco-map-interface-styles";
if (!document.getElementById(mapInterfaceStyleId)) {
  const styleSheet = document.createElement("style");
  styleSheet.id = mapInterfaceStyleId;
  styleSheet.innerText = styles;
  document.head.appendChild(styleSheet);
}

export default MapInterface;
